import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  Grid,
  Typography,
} from '@mui/material';
import { useGetDashboardQuery } from '../../../services/rtkQueryServices';
import { CountUp } from '../../../components/CountUp';
import { Calendar } from '../../../components/Calendar';
import { LineChart } from '../../../components/Charts/lineChart';
import { useAppSelector } from '../../../store/reduxHooks';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import EmailIcon from '@mui/icons-material/Email';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { v4 } from 'uuid';

export default function DashboardPage(props: any) {
  const { lightTheme } = useAppSelector((state) => state.ux);
  const [globalKey, setGlobalKey] = useState('some-key');
  const navigate = useNavigate();
  const { data: dashboard, refetch: refDashboard } = useGetDashboardQuery({});

  useEffect(() => {
    refDashboard();
    setGlobalKey(v4());
  }, []);

  const compiled = useMemo(() => {
    if (!dashboard) {
      return {};
    }
    let sponsor = dashboard.peopleBreakDownResp?.find(
      (item: any) => item._id === 'sponsor'
    )?.count;
    let attendee = dashboard.peopleBreakDownResp?.find(
      (item: any) => item._id === 'attendee'
    )?.count;
    let speaker = dashboard.peopleBreakDownResp?.find(
      (item: any) => item._id === 'speaker'
    )?.count;

    return {
      sponsor,
      attendee,
      speaker,
    };
  }, [dashboard]);

  const usersBreakdown: any = useMemo(() => {
    if (!dashboard || !dashboard.peopleBreakDownResp) {
      return { labels: [], series: [] };
    }
    return {
      labels: dashboard.peopleBreakDownResp?.map((item: any) => item?._id),
      series: dashboard.peopleBreakDownResp?.map((item: any) => item?.count),
    };
  }, [dashboard]);

  const userCreationSeries: any = useMemo(() => {
    if (!dashboard || !dashboard?.peopleByTimeResp) {
      return [];
    }
    let obj: any = {};
    dashboard.peopleByTimeResp?.map((item: any) => {
      const [type, year, month, day] = item?._id.split('-');
      const date = new Date(`${year}-${month}-${day}`);
      if (!obj[type]) {
        obj[type] = [[date.getTime(), item?.count]];
      } else {
        obj[type].push([date.getTime(), item?.count]);
      }
      return item;
    });
    return Object.keys(obj).map((key: string) => ({
      name: key,
      data: obj[key],
    }));
  }, [dashboard]);

  const userActivitySeries: any = useMemo(() => {
    if (!dashboard || !dashboard.usersActivityResp) {
      return [];
    }

    let data = dashboard.usersActivityResp?.map((item: any) => {
      return [new Date(item?.date), item?.count];
    });
    return [
      {
        name: 'User Activity',
        data: data,
      },
    ];
  }, [dashboard]);

  const contactUsList: any = useMemo(() => {
    if (!dashboard || !dashboard.contactUsListResp) {
      return [];
    }
    return dashboard.contactUsListResp;
  }, [dashboard]);

  const notificationsList: any = useMemo(() => {
    if (!dashboard || !dashboard.notificationsListResp) {
      return [];
    }
    return dashboard.notificationsListResp;
  }, [dashboard]);

  const typeChangeRequestList: any = useMemo(() => {
    if (!dashboard || !dashboard.typeSwitchRequestsSummaryResp) {
      return [];
    }
    return dashboard.typeSwitchRequestsSummaryResp;
  }, [dashboard]);

  const eventSessionsData: any = useMemo(() => {
    if (!dashboard || !dashboard.eventSessionsResp) {
      return [];
    }
    let result = dashboard.eventSessionsResp.map((e: any) => ({
      ...e,
      data: e.data.map((dataItem: any) => ({
        ...dataItem,
        y: [
          new Date(dataItem.y[0]).getTime(),
          new Date(dataItem.y[1]).getTime(),
        ],
      })),
    }));
    return result;
  }, [dashboard]);

  const totalCrs: any = useMemo(() => {
    if (!dashboard || !dashboard.typeSwitchRequestsByTimeResp) {
      return [];
    }
    if (dashboard.typeSwitchRequestsByTimeResp.length === 0) {
      return [];
    }
    return dashboard.typeSwitchRequestsByTimeResp
      .map((e: any) => e?.count)
      .reduce((k: any, v: any) => k + v);
  }, [dashboard]);

  const dashboardCardStyle: any = {
    fontSize: 60,
    fontWeight: 200,
    fontFamily: 'Roboto',
  };

  const cardPress = (path: string) =>
    useCallback(() => {
      navigate(path);
    }, []);

  const stasBar = useMemo(() => {
    return [
      {
        label: 'Sessions',
        route: 'sessions',
        icon: ({ style }: any) => <CastForEducationIcon style={style} />,
        value: dashboard?.numberOfSessionsResp,
      },
      {
        label: 'Speakers',
        route: 'speakers',
        icon: ({ style }: any) => <EmojiPeopleIcon style={style} />,
        value: compiled.speaker,
      },
      {
        label: 'Attendees',
        route: 'attendees',
        icon: ({ style }: any) => <GroupsIcon style={style} />,
        value: compiled.attendee,
      },
      {
        label: 'Sponsors',
        route: 'companies',
        icon: ({ style }: any) => <BusinessIcon style={style} />,
        value: compiled.sponsor,
      },
      {
        label: 'Type Change',
        route: 'change-type',
        icon: ({ style }: any) => <ChangeCircleIcon style={style} />,
        value: totalCrs,
      },
      {
        label: 'Messages',
        route: 'contact-us-messages',
        icon: ({ style }: any) => <EmailIcon style={style} />,
        value: dashboard?.contactUsListCountResp,
      },
    ];
  }, []);

  return (
    <>
      <Grid container spacing={2} key={globalKey}>
        {stasBar.map((item: any) => {
          const { icon: Icon } = item;
          return (
            <Grid item xs={6} md={4} lg={2} key={`stats-card-${item.route}`}>
              <Card sx={{ height: 150 }} onClick={cardPress(item.route)}>
                <CardActionArea>
                  <CardContent
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <CountUp
                      id='companies-count-up'
                      value={item.value}
                      style={dashboardCardStyle}
                    />
                    <Icon style={{ fontSize: 40, color: '#555' }} />
                  </CardContent>
                </CardActionArea>
                <CardActions>
                  <Typography variant='h5'>{item.label}</Typography>
                </CardActions>
              </Card>
            </Grid>
          );
        })}

        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <LineChart
                type={'line'}
                stacked={true}
                title='Users Activity'
                titlePos='left'
                height={300}
                datalabel={true}
                xAxis={{
                  type: 'datetime',
                }}
                series={userActivitySeries}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <LineChart
                type={'bar'}
                stacked={true}
                title='Users Registration this month'
                titlePos='left'
                height={300}
                xAxis={{
                  type: 'datetime',
                  min: new Date(moment().format('YYYY-MM-01')).getTime(),
                  max: new Date(
                    moment().add(1, 'M').format('YYYY-MM-01')
                  ).getTime(),
                }}
                legend={true}
                datalabel={true}
                series={userCreationSeries}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 355 }}>
            <CardActionArea>
              <CardContent>
                <LineChart
                  type='donut'
                  title='Users Breakdown'
                  labels={usersBreakdown.labels}
                  series={usersBreakdown.series}
                  legend={true}
                  legendPos={'bottom'}
                  height={355}
                  donut={{ donutShowTotal: true }}
                  datalabel={true}
                  titlePos='left'
                />
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardActionArea>
              <CardContent>
                <LineChart
                  type='rangeBar'
                  title='Event Sessions'
                  series={eventSessionsData}
                  height={300}
                  donut={{ donutShowTotal: true }}
                  datalabel={false}
                  titlePos='left'
                  plotOptions={{
                    bar: {
                      horizontal: true,
                      barHeight: '80%',
                    },
                  }}
                  xAxis={{ type: 'datetime' }}
                />
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 500 }}>
            <CardContent>
              <Typography variant='h5' style={{ marginBottom: 20 }}>
                Last 10 Notifications
              </Typography>
              <div style={{ height: 400, overflowY: 'scroll' }}>
                {notificationsList.map((item: any, idx: number) => (
                  <div
                    key={`last-messages=${idx}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderBottom: '1px solid rgba(0,0,0,0.2)',
                      padding: 5,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <Typography style={{ flex: 1, fontWeight: 500 }}>
                        {item.title}
                      </Typography>

                      <Typography variant='caption'>
                        {moment(item.date).fromNow()}
                      </Typography>
                    </div>
                    <Typography style={{ marginLeft: 50 }}>
                      {item.message}
                    </Typography>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 500 }}>
            <CardContent>
              <Typography variant='h5' style={{ marginBottom: 20 }}>
                Last 10 Messages
              </Typography>
              <div style={{ height: 400, overflowY: 'scroll' }}>
                {contactUsList.map((item: any, idx: number) => (
                  <div
                    key={`last-messages=${idx}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderBottom: '1px solid rgba(0,0,0,0.2)',
                      padding: 5,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <Avatar
                        alt={item.name}
                        src={
                          item.uuid
                            ? `/api/admin/v1/file/profile/${item.uuid}`
                            : ''
                        }
                        style={{ border: '1px solid rgba(0,0,0,0.3)' }}
                      />
                      <Typography style={{ flex: 1, fontWeight: 500 }}>
                        {item.name}
                      </Typography>

                      <Typography variant='caption'>
                        {moment(item.createdByTime).fromNow()}
                      </Typography>
                    </div>
                    <Typography style={{ marginLeft: 50 }}>
                      {item.message}
                    </Typography>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 500 }}>
            <CardContent>
              <Typography variant='h5' style={{ marginBottom: 20 }}>
                Change Requests
              </Typography>
              <div style={{ height: 400, overflowY: 'scroll' }}>
                {typeChangeRequestList.map((item: any, idx: number) => (
                  <div
                    key={`last-messages=${idx}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      borderBottom: '1px solid rgba(0,0,0,0.2)',
                      padding: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <Avatar
                        alt={item.name}
                        src={
                          item.uuid
                            ? `/api/admin/v1/file/profile/${item.uuid}`
                            : ''
                        }
                        style={{ border: '1px solid rgba(0,0,0,0.3)' }}
                      />
                      <Typography style={{ flex: 1, fontWeight: 500 }}>
                        {item.name}
                      </Typography>

                      <Typography variant='caption'>
                        {moment(item.typeSwitchRequest.date).fromNow()}
                      </Typography>
                    </div>
                    <Box style={{ marginLeft: 40, display: 'flex' }}>
                      <Chip label={item.type} size={'small'} color='info' />
                      <ArrowRightAltIcon />
                      <Chip
                        label={item.typeSwitchRequest.type}
                        size={'small'}
                        color='primary'
                      />
                    </Box>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12}>
          <Calendar />
        </Grid> */}
      </Grid>
    </>
  );
}
